import React from "react";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";

export const Pagination = ({
  totalPages = 10,
  currentPage = 1,
  onPageChange = () => {},
  showPages = 5,
}) => {
  const getVisiblePages = () => {
    const pages = [];
    const half = Math.floor(showPages / 2);

    let start = Math.max(currentPage - half, 1);
    let end = Math.min(start + showPages - 1, totalPages);

    if (end - start + 1 < showPages) {
      start = Math.max(end - showPages + 1, 1);
    }

    // Always show first page
    if (start > 1) {
      pages.push(1);
      if (start > 2) {
        pages.push("...");
      }
    }

    // Add visible pages
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Always show last page
    if (end < totalPages) {
      if (end < totalPages - 1) {
        pages.push("...");
      }
      pages.push(totalPages);
    }

    return pages;
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);

      // Update the URL with current page (keeps other params intact)
      const url = new URL(window.location.href);
      url.searchParams.set("page", String(page));
      window.history.pushState({}, "", url);

      // Smooth scroll to top
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="flex items-center justify-center space-x-1 p-4">
      {/* Previous Button */}
      <button
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`
          flex items-center justify-center w-10 h-10 rounded-lg border transition-all duration-200
          ${
            currentPage === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
              : "bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-gray-300 hover:border-gray-400"
          }
        `}
      >
        <ChevronLeft size={18} />
      </button>

      {/* Page Numbers */}
      <div className="flex items-center space-x-1">
        {visiblePages.map((page, index) => (
          <React.Fragment key={index}>
            {page === "..." ? (
              <div className="flex items-center justify-center w-10 h-10">
                <MoreHorizontal size={18} className="text-gray-400" />
              </div>
            ) : (
              <button
                onClick={() => handlePageChange(page)}
                className={`
                  flex items-center justify-center w-10 h-10 rounded-lg border transition-all duration-200 font-medium
                  ${
                    page === currentPage
                      ? "bg-blue-600 text-white border-blue-600 shadow-md"
                      : "bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-gray-300 hover:border-gray-400"
                  }
                `}
              >
                {page}
              </button>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Next Button */}
      <button
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`
          flex items-center justify-center w-10 h-10 rounded-lg border transition-all duration-200
          ${
            currentPage === totalPages
              ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
              : "bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-gray-300 hover:border-gray-400"
          }
        `}
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
};
